import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Play, Pause, RotateCcw, Volume2, VolumeX, ChevronRight } from 'lucide-react';
import { PrimaryButton, SecondaryButton, IconButton } from '../components/brand/BrandButton';
import { Heading, BodyText, SmallText } from '../components/brand/BrandTypography';
import AudioTip from '../components/brand/AudioTip';
import { useMeditationContent } from '../hooks/useMeditationContent';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Progressive Muscle Relaxation (PMR)
 * Systematic tension-release exercise for physical and emotional regulation
 * Audio-guided with visual muscle group indicator
 */

// Default steps (fallback if API fails)
const DEFAULT_PMR_STEPS = [
    {
        id: 'intro',
        muscleGroup: 'Introduction',
        duration: 20,
        tensionInstruction: null,
        releaseInstruction: "Find a comfortable position. . . either sitting or lying down. . . Close your eyes if that feels safe. . . Take three deep breaths.",
        guidance: "This practice involves tensing and releasing muscle groups. . . You'll hold tension for about 5 seconds. . . then release. . . and notice the difference."
    },
    {
        id: 'hands',
        muscleGroup: 'Hands',
        duration: 25,
        tensionInstruction: "Make tight fists with both hands. . . Squeeze hard. . . Feel the tension in your fingers. . . palms. . . and forearms.",
        releaseInstruction: "Now release. . . Let your hands fall open. . . Notice the warmth. . . the tingling. . . the relief.",
        guidance: "The contrast between tension and release. . . teaches your nervous system. . . what relaxation feels like."
    },
    {
        id: 'arms',
        muscleGroup: 'Arms',
        duration: 25,
        tensionInstruction: "Bend your elbows. . . and flex your biceps. . . like you're showing off your muscles. . . Hold that tension.",
        releaseInstruction: "Release. . . Let your arms drop. . . Feel the heaviness. . . the ease.",
        guidance: "Your arms carry so much. . . lifting. . . holding. . . protecting. . . Let them rest."
    },
    {
        id: 'shoulders',
        muscleGroup: 'Shoulders',
        duration: 25,
        tensionInstruction: "Raise your shoulders up toward your ears. . . Squeeze them up tight. . . Hold.",
        releaseInstruction: "Let them drop. . . Feel them sink down. . . heavy and relaxed.",
        guidance: "We carry tension here without realizing. . . Notice how different it feels. . . to let go."
    },
    {
        id: 'face-forehead',
        muscleGroup: 'Forehead',
        duration: 20,
        tensionInstruction: "Raise your eyebrows as high as you can. . . Wrinkle your forehead. . . Hold the tension.",
        releaseInstruction: "Release. . . Let your forehead smooth out. . . Feel the skin relax.",
        guidance: "We hold worry in our foreheads. . . Let it soften."
    },
    {
        id: 'face-eyes',
        muscleGroup: 'Eyes',
        duration: 20,
        tensionInstruction: "Squeeze your eyes shut tightly. . . Really scrunch them closed.",
        releaseInstruction: "Release. . . Keep your eyes gently closed. . . but relaxed. . . Feel the ease around your eyes.",
        guidance: "Our eyes work so hard. . . Give them permission to rest."
    },
    {
        id: 'face-jaw',
        muscleGroup: 'Jaw',
        duration: 20,
        tensionInstruction: "Clench your jaw. . . Press your teeth together. . . Feel the tension in your jaw muscles.",
        releaseInstruction: "Let your jaw drop open slightly. . . Let your tongue rest. . . Feel the relief.",
        guidance: "The jaw holds what we haven't said. . . Release those unspoken words."
    },
    {
        id: 'neck',
        muscleGroup: 'Neck',
        duration: 25,
        tensionInstruction: "Gently press your head back. . . against your chair or pillow. . . Feel the tension in the back of your neck.",
        releaseInstruction: "Release. . . Let your head rest naturally. . . Feel the neck muscles soften.",
        guidance: "Be gentle here. . . The neck is vulnerable. . . and precious."
    },
    {
        id: 'chest',
        muscleGroup: 'Chest',
        duration: 25,
        tensionInstruction: "Take a deep breath. . . and hold it. . . Feel your chest expand. . . and the muscles tighten.",
        releaseInstruction: "Exhale slowly. . . and completely. . . Feel your chest soften. . . and relax.",
        guidance: "The chest holds our heart. . . Let it open. . . Let it breathe."
    },
    {
        id: 'stomach',
        muscleGroup: 'Stomach',
        duration: 25,
        tensionInstruction: "Tighten your stomach muscles. . . Pull your belly button toward your spine.",
        releaseInstruction: "Release. . . Let your belly be soft. . . Let it rise and fall naturally.",
        guidance: "Our gut holds intuition and emotion. . . What does yours want to tell you?"
    },
    {
        id: 'back',
        muscleGroup: 'Back',
        duration: 25,
        tensionInstruction: "Arch your back slightly. . . pressing your shoulders back. . . Feel the tension along your spine.",
        releaseInstruction: "Release. . . Let your back settle. . . against the surface beneath you.",
        guidance: "The back carries what we don't want to see. . . Let it rest."
    },
    {
        id: 'glutes',
        muscleGroup: 'Glutes & Hips',
        duration: 20,
        tensionInstruction: "Squeeze your buttocks together. . . Hold the tension in your seat and hips.",
        releaseInstruction: "Release. . . Feel yourself sink down. . . supported and relaxed.",
        guidance: "We sit on tension without knowing. . . Let it go."
    },
    {
        id: 'thighs',
        muscleGroup: 'Thighs',
        duration: 25,
        tensionInstruction: "Tense your thigh muscles. . . Press your legs together. . . or against each other.",
        releaseInstruction: "Release. . . Feel the heaviness in your legs. . . Let them be still.",
        guidance: "Your legs carry you through the world. . . Let them rest."
    },
    {
        id: 'calves',
        muscleGroup: 'Calves',
        duration: 20,
        tensionInstruction: "Point your toes down. . . away from your body. . . Feel the tension in your calves.",
        releaseInstruction: "Release. . . Let your feet relax back to neutral.",
        guidance: "Almost done. . . You're doing beautifully."
    },
    {
        id: 'feet',
        muscleGroup: 'Feet',
        duration: 20,
        tensionInstruction: "Curl your toes under. . . Squeeze them tight. . . Feel the tension in your feet.",
        releaseInstruction: "Release. . . Spread your toes out. . . Feel them relax.",
        guidance: "Your feet ground you to the earth. . . Feel that connection."
    },
    {
        id: 'closing',
        muscleGroup: 'Full Body Integration',
        duration: 30,
        tensionInstruction: null,
        releaseInstruction: "Now. . . feel your entire body at once. . . Notice the overall sense of relaxation. . . You've just released tension. . . from head to toe.",
        guidance: "Take a moment. . . to appreciate this feeling. . . Your body knows how to relax. . . You've just reminded it. . . Carry this calm with you."
    }
];

const ProgressiveMuscleRelaxation = () => {
    const navigate = useNavigate();
    
    // Fetch content from database (falls back to defaults)
    const { steps: PMR_STEPS, loading: contentLoading } = useMeditationContent('pmr-main', DEFAULT_PMR_STEPS);
    
    const [currentStep, setCurrentStep] = useState(0);
    const [phase, setPhase] = useState('ready'); // ready, tension, release
    const [isPlaying, setIsPlaying] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const [timeRemaining, setTimeRemaining] = useState(DEFAULT_PMR_STEPS[0].duration);
    const [audioEnabled, setAudioEnabled] = useState(true);
    const [isComplete, setIsComplete] = useState(false);
    const [isAudioPlaying, setIsAudioPlaying] = useState(false);
    const [musicEnabled, setMusicEnabled] = useState(true);
    const [musicVolume] = useState(0.35); // Keep music soft behind voice
    const audioRef = useRef(null);
    const musicRef = useRef(null);
    const timerRef = useRef(null);

    // Update timeRemaining when steps load from API
    useEffect(() => {
        if (PMR_STEPS.length > 0 && !isPlaying) {
            setTimeRemaining(PMR_STEPS[0].duration);
        }
    }, [PMR_STEPS, isPlaying]);

    const step = PMR_STEPS[currentStep] || DEFAULT_PMR_STEPS[0];
    const progress = ((currentStep + 1) / PMR_STEPS.length) * 100;
    const totalDuration = PMR_STEPS.reduce((sum, s) => sum + s.duration, 0);

    // Background music control
    useEffect(() => {
        if (musicRef.current) {
            musicRef.current.volume = musicVolume;
            musicRef.current.loop = true;
        }
    }, [musicVolume]);

    // Start/stop background music with session
    useEffect(() => {
        if (musicRef.current && musicEnabled) {
            if (isPlaying && !isPaused) {
                musicRef.current.play().catch(() => {});
            } else {
                musicRef.current.pause();
            }
        }
    }, [isPlaying, isPaused, musicEnabled]);

    // Lower music volume during voice guidance
    useEffect(() => {
        if (musicRef.current && musicEnabled) {
            if (isAudioPlaying) {
                musicRef.current.volume = musicVolume * 0.3; // Duck music during voice
            } else {
                musicRef.current.volume = musicVolume;
            }
        }
    }, [isAudioPlaying, musicEnabled, musicVolume]);

    // Generate audio for current instruction - returns a promise
    const generateAudio = async (text) => {
        if (!audioEnabled || !text) return Promise.resolve();
        
        // Stop any currently playing audio first
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
        }
        
        setIsAudioPlaying(true);
        
        try {
            const response = await fetch(`${API_URL}/api/tts/generate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    text: text,
                    voice: 'alloy',
                    speed: 0.85
                })
            });
            
            if (response.ok) {
                const data = await response.json();
                if (audioRef.current) {
                    return new Promise((resolve) => {
                        audioRef.current.src = `data:audio/mp3;base64,${data.audio}`;
                        audioRef.current.onended = () => {
                            setIsAudioPlaying(false);
                            resolve();
                        };
                        audioRef.current.onerror = () => {
                            setIsAudioPlaying(false);
                            resolve();
                        };
                        audioRef.current.play().catch(() => {
                            setIsAudioPlaying(false);
                            resolve();
                        });
                    });
                }
            }
        } catch (error) {
            console.error('Audio generation failed:', error);
            setIsAudioPlaying(false);
        }
        return Promise.resolve();
    };

    // Timer logic - only counts down when audio is not playing
    useEffect(() => {
        if (isPlaying && !isPaused && !isAudioPlaying) {
            timerRef.current = setInterval(() => {
                setTimeRemaining(prev => {
                    if (prev <= 1) {
                        clearInterval(timerRef.current);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        } else {
            if (timerRef.current) clearInterval(timerRef.current);
        }
        
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, [isPlaying, isPaused, isAudioPlaying]);

    // Handle phase transitions when timer reaches 0
    useEffect(() => {
        if (timeRemaining === 0 && isPlaying && !isPaused) {
            handleNextPhase();
        }
    }, [timeRemaining, isPlaying, isPaused]);

    const handleNextPhase = async () => {
        if (phase === 'tension' && step.releaseInstruction) {
            // Move to release phase
            setPhase('release');
            await generateAudio(step.releaseInstruction);
            setTimeRemaining(12); // Time after audio to hold the release
        } else {
            // Move to next step
            if (currentStep < PMR_STEPS.length - 1) {
                const nextStep = PMR_STEPS[currentStep + 1];
                setCurrentStep(currentStep + 1);
                const hasTension = nextStep.tensionInstruction;
                setPhase(hasTension ? 'tension' : 'release');
                const instruction = hasTension ? nextStep.tensionInstruction : nextStep.releaseInstruction;
                await generateAudio(instruction);
                setTimeRemaining(hasTension ? 10 : nextStep.duration); // Time to hold after instruction
            } else {
                setIsPlaying(false);
                setIsComplete(true);
            }
        }
    };

    const handleStart = async () => {
        setIsPlaying(true);
        setIsPaused(false);
        const hasTension = step.tensionInstruction;
        setPhase(hasTension ? 'tension' : 'release');
        const instruction = hasTension ? step.tensionInstruction : step.releaseInstruction;
        await generateAudio(instruction);
        setTimeRemaining(hasTension ? 10 : step.duration);
    };

    const handlePause = () => {
        setIsPaused(!isPaused);
        if (audioRef.current) {
            if (!isPaused) {
                audioRef.current.pause();
            } else {
                audioRef.current.play();
            }
        }
    };

    const handleReset = () => {
        setCurrentStep(0);
        setTimeRemaining(PMR_STEPS[0].duration);
        setPhase('ready');
        setIsPlaying(false);
        setIsPaused(false);
        setIsComplete(false);
        setIsAudioPlaying(false);
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.src = '';
        }
        if (musicRef.current) {
            musicRef.current.pause();
            musicRef.current.currentTime = 0;
        }
    };

    // Completion screen
    if (isComplete) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-[#E8F5F5] to-[#FEF6EF]">
                <header className="p-4">
                    <IconButton onClick={() => navigate('/home')} data-testid="close-btn">
                        <ArrowLeft className="w-5 h-5" />
                    </IconButton>
                </header>
                
                <main className="max-w-md mx-auto px-6 py-12 text-center">
                    <div className="w-20 h-20 rounded-full bg-[#7D8E95]/30 flex items-center justify-center mx-auto mb-6">
                        <div className="w-12 h-12 rounded-full bg-[#7D8E95] flex items-center justify-center">
                            <span className="text-white text-2xl">✓</span>
                        </div>
                    </div>
                    
                    <Heading level={1} className="text-[#1D6B6B] mb-4">
                        PMR Complete
                    </Heading>
                    
                    <BodyText className="text-[#7D8E95] mb-8 leading-relaxed">
                        You've systematically released tension from your entire body. 
                        Notice how different you feel now compared to when you started.
                    </BodyText>
                    
                    <div className="space-y-3">
                        <PrimaryButton onClick={() => navigate('/home')} className="w-full">
                            Return to Dashboard
                        </PrimaryButton>
                        <SecondaryButton onClick={handleReset} className="w-full">
                            Do Another Session
                        </SecondaryButton>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-[#E8F5F5] to-[#FEF6EF]">
            {/* Hidden audio elements */}
            <audio ref={audioRef} />
            <audio ref={musicRef} src="/audio/ocean-peace.mp3" loop preload="auto" />
            
            {/* Header */}
            <header className="sticky top-0 z-40 glass-header p-4">
                <div className="flex items-center justify-between">
                    <IconButton onClick={() => navigate(-1)} data-testid="back-btn">
                        <ArrowLeft className="w-5 h-5" />
                    </IconButton>
                    <SmallText className="text-[#7D8E95]">Progressive Muscle Relaxation</SmallText>
                    {/* Audio toggle - moved to avoid overlap with global menu */}
                    <div className="w-11" /> {/* Spacer */}
                </div>
                
                {/* Audio control row */}
                <div className="mt-3 flex items-center justify-center gap-2 p-1.5 bg-[#FFBB98]/40 rounded-2xl w-fit mx-auto">
                    <button
                        onClick={() => setAudioEnabled(!audioEnabled)}
                        className={`flex items-center gap-2 px-4 py-2.5 rounded-xl transition-all duration-300 ${
                            audioEnabled 
                                ? 'bg-white/90 backdrop-blur-sm text-[#1D6B6B] shadow-soft' 
                                : 'text-[#7D8E95] hover:text-[#2A8585] hover:bg-white/40'
                        }`}
                        data-testid="audio-toggle"
                    >
                        {audioEnabled ? (
                            <>
                                <Volume2 className="w-4 h-4" />
                                <SmallText>Voice On</SmallText>
                            </>
                        ) : (
                            <>
                                <VolumeX className="w-4 h-4" />
                                <SmallText>Voice Off</SmallText>
                            </>
                        )}
                    </button>
                    <button
                        onClick={() => {
                            setMusicEnabled(!musicEnabled);
                            if (musicRef.current) {
                                if (!musicEnabled && isPlaying) {
                                    musicRef.current.play().catch(() => {});
                                } else {
                                    musicRef.current.pause();
                                }
                            }
                        }}
                        className={`flex items-center gap-2 px-4 py-2.5 rounded-xl transition-all duration-300 ${
                            musicEnabled 
                                ? 'bg-white/90 backdrop-blur-sm text-[#1D6B6B] shadow-soft' 
                                : 'text-[#7D8E95] hover:text-[#2A8585] hover:bg-white/40'
                        }`}
                        data-testid="music-toggle"
                    >
                        {musicEnabled ? (
                            <SmallText>Music On</SmallText>
                        ) : (
                            <SmallText>Music Off</SmallText>
                        )}
                    </button>
                </div>
                
                {/* Progress bar */}
                <div className="mt-3 h-1.5 bg-[#7D8E95] rounded-full overflow-hidden">
                    <div 
                        className="h-full bg-[#7D8E95] rounded-full transition-all duration-500"
                        style={{ width: `${progress}%` }}
                    />
                </div>
            </header>

            <main className="max-w-md mx-auto px-6 py-8">
                {/* Phase indicator */}
                {isPlaying && phase !== 'ready' && (
                    <div className={`text-center mb-6 py-4 rounded-2xl transition-all duration-500 ${
                        phase === 'tension' 
                            ? 'bg-[#FFCDD2]/50 border border-[#FFCDD2]' 
                            : 'bg-[#FFBB98]/50 border border-white/40'
                    }`}>
                        <SmallText className={`font-bold text-lg ${
                            phase === 'tension' ? 'text-[#C62828]' : 'text-[#1D6B6B]'
                        }`}>
                            {phase === 'tension' ? 'TENSE' : 'RELEASE'}
                        </SmallText>
                    </div>
                )}

                {/* Muscle group indicator */}
                <div className="text-center mb-8">
                    <SmallText className="text-[#7D8E95] font-medium mb-2">
                        {currentStep + 1} of {PMR_STEPS.length}
                    </SmallText>
                    <Heading level={2} className="text-[#1D6B6B] mb-4">
                        {step.muscleGroup}
                    </Heading>
                </div>

                {/* Current instruction */}
                <div className="bg-white/50 rounded-2xl p-6 mb-6">
                    <BodyText className="text-[#1D6B6B] leading-relaxed mb-4">
                        {phase === 'tension' && step.tensionInstruction 
                            ? step.tensionInstruction 
                            : step.releaseInstruction}
                    </BodyText>
                    <SmallText className="text-[#7D8E95] italic">
                        {step.guidance}
                    </SmallText>
                </div>

                {/* Timer */}
                {isPlaying && (
                    <div className="text-center mb-6">
                        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${
                            phase === 'tension' ? 'bg-[#FFCDD2]/30' : 'bg-[#FFBB98]/30'
                        }`}>
                            <div className={`w-2 h-2 rounded-full ${
                                isPaused ? 'bg-yellow-500' : phase === 'tension' ? 'bg-[#C62828]' : 'bg-[#1D6B6B]'
                            } ${!isPaused && 'animate-pulse'}`} />
                            <SmallText className="text-[#7D8E95] font-mono">
                                {timeRemaining}s
                            </SmallText>
                        </div>
                    </div>
                )}

                {/* Controls */}
                <div className="flex flex-col items-center gap-4 mb-8">
                    {!isPlaying && (
                        <AudioTip variant="headphones" className="max-w-sm" />
                    )}
                    
                    <div className="flex justify-center gap-4">
                        {!isPlaying ? (
                            <PrimaryButton onClick={handleStart} size="lg" data-testid="start-btn" className="bg-[#7D8E95] hover:bg-[#7D8E95]">
                                <Play className="w-5 h-5 mr-2" />
                                Begin PMR
                            </PrimaryButton>
                        ) : (
                            <>
                                <IconButton onClick={handleReset} className="bg-white/50" data-testid="reset-btn">
                                    <RotateCcw className="w-5 h-5" />
                                </IconButton>
                                <PrimaryButton onClick={handlePause} data-testid="pause-btn" className="bg-[#7D8E95] hover:bg-[#7D8E95]">
                                    {isPaused ? <Play className="w-5 h-5" /> : <Pause className="w-5 h-5" />}
                                </PrimaryButton>
                            </>
                        )}
                    </div>
                </div>

                {/* Step indicators */}
                <div className="flex flex-wrap justify-center gap-2">
                    {PMR_STEPS.map((s, index) => (
                        <div
                            key={s.id}
                            className={`w-3 h-3 rounded-full transition-all ${
                                index === currentStep 
                                    ? 'bg-[#7D8E95] scale-125' 
                                    : index < currentStep 
                                        ? 'bg-[#7D8E95]/50' 
                                        : 'bg-[#7D8E95]'
                            }`}
                            title={s.muscleGroup}
                        />
                    ))}
                </div>
            </main>
        </div>
    );
};

export default ProgressiveMuscleRelaxation;
